import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Divider,
  LinearProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import API from '../../../services/API'

const YesNoQuestion = ({ item, onChange }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 2,
      border: '1px solid #E2E8F0',
      bgcolor: '#F8FAFC',
    }}
  >
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      spacing={1.5}
    >
      <Typography sx={{ color: '#242F40', fontWeight: 500 }}>
        {item.question}
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={item.answer || null}
        onChange={(e, newValue) => {
          if (!newValue) return
          onChange({
            answer: newValue,
            comment: newValue === item.flagOn ? item.comment : '',
          })
        }}
      >
        <ToggleButton
          value="Yes"
          sx={{
            fontWeight: 700,
            px: 2.5,
            '&.Mui-selected': {
              bgcolor: item.flagOn === 'Yes' ? '#E32525' : '#00964C',
              color: '#FFFFFF',
              '&:hover': {
                bgcolor: item.flagOn === 'Yes' ? '#7F1D1D' : '#065F46',
              },
            },
          }}
        >
          <CheckRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
          Yes
        </ToggleButton>
        <ToggleButton
          value="No"
          sx={{
            fontWeight: 700,
            px: 2.5,
            '&.Mui-selected': {
              bgcolor: item.flagOn === 'No' ? '#E32525' : '#00964C',
              color: '#FFFFFF',
              '&:hover': {
                bgcolor: item.flagOn === 'No' ? '#7F1D1D' : '#065F46',
              },
            },
          }}
        >
          <CloseRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
          No
        </ToggleButton>
      </ToggleButtonGroup>
    </Stack>

    {item.answer && item.answer === item.flagOn && (
      <TextField
        fullWidth
        multiline
        minRows={2}
        size="small"
        placeholder="Please provide details"
        value={item.comment || ''}
        onChange={(e) => onChange({ comment: e.target.value })}
        sx={{ mt: 1.5, bgcolor: '#FFFFFF' }}
      />
    )}
  </Box>
)

// Older settings may still store these as plain strings (before per-question
// polarity was added), or as objects mangled by that shape change - normalize
// anything we're given to a clean { question, flagOn } shape.
const normalizeQuestion = (q) => {
  if (typeof q === 'string') return { question: q, flagOn: 'Yes' }
  return {
    question: typeof q?.question === 'string' ? q.question : '',
    flagOn: q?.flagOn === 'No' ? 'No' : 'Yes',
  }
}

const PreAppraisalForm = ({ appraisal, onSaved }) => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [fitnessToPractice, setFitnessToPractice] = useState([])
  const [occupationalHealth, setOccupationalHealth] = useState([])

  useEffect(() => {
    API.get('/settings?select=staffAppraisalSettings').then((res) => {
      if (res.status === 200) {
        const questions = res.data?.staffAppraisalSettings?.preAppraisalQuestions
        setFitnessToPractice(
          (questions?.fitnessToPractice ?? [])
            .map((q) => ({ ...normalizeQuestion(q), answer: '', comment: '' }))
            .filter((q) => q.question),
        )
        setOccupationalHealth(
          (questions?.occupationalHealth ?? [])
            .map((q) => ({ ...normalizeQuestion(q), answer: '', comment: '' }))
            .filter((q) => q.question),
        )
      }
      setLoading(false)
    })
  }, [])

  const updateAt = (setter, index, changes) => {
    setter((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, ...changes } : item)),
    )
  }

  const allAnswered =
    fitnessToPractice.every((item) => item.answer) &&
    occupationalHealth.every((item) => item.answer)

  const handleSubmit = () => {
    setSaving(true)
    API.put(`/appraisals/${appraisal._id}/pre-appraisal`, {
      fitnessToPractice,
      occupationalHealth,
    })
      .then((res) => {
        if (res.status === 200) {
          onSaved(res.data)
        }
      })
      .finally(() => setSaving(false))
  }

  if (loading) return <LinearProgress />

  return (
    <Stack gap={3}>
      <Stack gap={1.5}>
        <Typography variant="title3">Fitness to Practice</Typography>
        {fitnessToPractice.map((item, i) => (
          <YesNoQuestion
            key={i}
            item={item}
            onChange={(changes) => updateAt(setFitnessToPractice, i, changes)}
          />
        ))}
      </Stack>

      <Divider />

      <Stack gap={1.5}>
        <Typography variant="title3">Occupational Health</Typography>
        {occupationalHealth.map((item, i) => (
          <YesNoQuestion
            key={i}
            item={item}
            onChange={(changes) => updateAt(setOccupationalHealth, i, changes)}
          />
        ))}
      </Stack>

      <Button
        variant="contained"
        size="large"
        onClick={handleSubmit}
        disabled={saving || !allAnswered}
      >
        Save &amp; Continue
      </Button>
    </Stack>
  )
}

export default PreAppraisalForm
